import React from 'react';
import { Box, Flex, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { motion } from 'framer-motion';

const MotionBox = motion.create(Box);

const livePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.5); }
  50% { box-shadow: 0 0 0 6px rgba(72, 187, 120, 0); }
`;

const BAR_HEIGHTS = [35, 55, 45, 80, 60, 90, 70];
const BAR_COLORS = ['cyan.500', 'cyan.400', 'cyan.500', 'cyan.300', 'cyan.400', 'cyan.300', 'cyan.400'];

const Bar = ({ h, color, flex = 1, delay = 0 }) => (
	<MotionBox
		flex={flex}
		rounded='sm'
		bg={color}
		initial={{ height: 0, opacity: 0 }}
		animate={{ height: h, opacity: 0.9 }}
		transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
	/>
);

export default function HomePageHeroPreview() {
	return (
		<Box
			rounded='2xl'
			overflow='hidden'
			boxShadow='0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08), 0 0 60px rgba(34,211,238,0.08)'
			bg='#0b1220'
			border='1px solid'
			borderColor='whiteAlpha.200'
			position='relative'
			_before={{
				content: '""',
				position: 'absolute',
				inset: 0,
				bgGradient: 'linear(to-br, rgba(34,211,238,0.06), transparent 50%, rgba(167,139,250,0.05))',
				pointerEvents: 'none',
				zIndex: 0,
			}}>
			<Flex
				align='center'
				justify='space-between'
				px={4}
				py={3}
				bg='whiteAlpha.50'
				borderBottom='1px solid'
				borderColor='whiteAlpha.100'
				position='relative'
				zIndex={1}>
				<HStack spacing={2}>
					{['#ff5f57', '#febc2e', '#28c840'].map((c) => (
						<Box key={c} w={3} h={3} borderRadius='full' bg={c} />
					))}
				</HStack>
				<Text fontSize='xs' color='whiteAlpha.600' mb={0} fontFamily='mono'>
					app.opspilotcrm.com
				</Text>
				<Box w={12} />
			</Flex>

			<Flex minH={{ base: '260px', md: '320px' }} position='relative' zIndex={1}>
				<Box
					w='56px'
					bg='whiteAlpha.50'
					borderRight='1px solid'
					borderColor='whiteAlpha.100'
					py={4}
					display={{ base: 'none', sm: 'block' }}>
					<VStack spacing={3}>
						{['cyan.400', 'whiteAlpha.300', 'whiteAlpha.300', 'whiteAlpha.300', 'whiteAlpha.300'].map(
							(c, i) => (
								<Box
									key={i}
									w={i === 0 ? 6 : 5}
									h={i === 0 ? 6 : 5}
									borderRadius='md'
									bg={c}
									opacity={i === 0 ? 1 : 0.5}
								/>
							),
						)}
					</VStack>
				</Box>

				<Box flex={1} p={{ base: 4, md: 5 }}>
					<HStack justify='space-between' mb={4}>
						<Box>
							<Text fontSize='xs' color='whiteAlpha.500' mb={1} textTransform='uppercase' letterSpacing='wider'>
								Dashboard
							</Text>
							<Text fontSize='md' fontWeight='bold' color='white' mb={0}>
								Good morning, Alex
							</Text>
						</Box>
						<Box
							px={3}
							py={1}
							rounded='full'
							bg='green.500'
							color='white'
							fontSize='xs'
							fontWeight='bold'
							sx={{ animation: `${livePulse} 2s ease-in-out infinite` }}>
							● Live
						</Box>
					</HStack>

					<SimpleGrid columns={3} spacing={3} mb={4}>
						{[
							{ label: 'Campaigns', val: '24', color: 'cyan.400' },
							{ label: 'Active clients', val: '18', color: 'purple.400' },
							{ label: 'Revenue', val: '$42k', color: 'green.400' },
						].map((s, i) => (
							<MotionBox
								key={s.label}
								p={3}
								rounded='lg'
								bg='whiteAlpha.50'
								border='1px solid'
								borderColor='whiteAlpha.100'
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.15 + i * 0.08, duration: 0.45 }}>
								<Text fontSize='10px' color='whiteAlpha.500' mb={1}>
									{s.label}
								</Text>
								<Text fontSize='lg' fontWeight='bold' color={s.color} mb={0}>
									{s.val}
								</Text>
							</MotionBox>
						))}
					</SimpleGrid>

					<Box
						p={3}
						rounded='lg'
						bg='whiteAlpha.50'
						border='1px solid'
						borderColor='whiteAlpha.100'>
						<Text fontSize='10px' color='whiteAlpha.500' mb={2}>
							Campaign performance
						</Text>
						<Flex align='flex-end' gap={2} h='72px'>
							{BAR_HEIGHTS.map((h, i) => (
								<Bar
									key={i}
									h={`${h}%`}
									color={BAR_COLORS[i]}
									flex={i === BAR_HEIGHTS.length - 1 ? 1.2 : 1}
									delay={0.4 + i * 0.07}
								/>
							))}
						</Flex>
					</Box>
				</Box>
			</Flex>
		</Box>
	);
}
